import { BuildCircle, Error as ErrorIcon, Info, NewReleases } from '@mui/icons-material';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    styled,
    TextField,
} from '@mui/material';
import { isNumber } from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';
import { useCreateNewAnnouncement } from 'src/hooks/announcements/create/useCreateAnnouncement';
import { useUpdateAnnouncement } from 'src/hooks/announcements/update/useUpdateAnnouncement';
import { AnnouncementTypeEnum } from 'src/infrastructure/rest-api/api-types';
import { DialogTitleWithCloseIcon } from '../DialogTitleWithCloseIcon/DialogTitleWithCloseIcon';

interface Props {
    id: number | undefined;
    title: string | undefined;
    description: string | undefined;
    type: AnnouncementTypeEnum | undefined;
    resolved: boolean;
    open: boolean;
    handleCloseDialog: () => void;
    handleRefetch: () => void;
}

export const AnnouncementDialog: FC<Props> = ({
    id,
    title: announcementTitle,
    description: announcementDescription,
    type: announcementType,
    resolved: announcementResolved,
    open,
    handleCloseDialog,
    handleRefetch,
}) => {
    const [titleError, setTitleError] = useState(false);
    const [descriptionError, setDescriptionError] = useState(false);
    const [title, setTitle] = useState(announcementTitle ?? '');
    const [description, setDescription] = useState(announcementDescription ?? '');
    const [type, setType] = useState(announcementType ?? 'Information');
    const [resolved, setResolved] = useState<boolean>(announcementResolved ?? false);
    const [dialogOpen, setDialogOpen] = useState(open ?? false);

    const [createAnnouncement, { loading: creatingAnnouncement }] = useCreateNewAnnouncement({
        title,
        description,
        type,
    });

    const [updateAnnouncement, { loading: updatingAnnouncement }] = useUpdateAnnouncement({
        id,
        title,
        description,
        type,
        resolved,
    });

    const loading = updatingAnnouncement || creatingAnnouncement;

    const canSave = useCallback(() => {
        if (!title.length || !description.length || !type) return false;
        return true;
    }, [title, description, type]);

    const closeDialogue = useCallback(() => {
        setDialogOpen(false);
        handleCloseDialog();
    }, [handleCloseDialog]);

    const resetAnnouncement = useCallback(() => {
        setTitle('');
        setDescription('');
        setType('Information');
        setResolved(false);
        setTitleError(false);
        setDescriptionError(false);
    }, []);

    const onCancel = useCallback(() => {
        closeDialogue();
        resetAnnouncement();
    }, [closeDialogue, resetAnnouncement]);

    const handleSaveAnnouncement = useCallback(async () => {
        if (id !== undefined) {
            await updateAnnouncement();
        } else {
            await createAnnouncement();
        }

        handleRefetch();
        closeDialogue();
        resetAnnouncement();
    }, [closeDialogue, createAnnouncement, handleRefetch, id, resetAnnouncement, updateAnnouncement]);

    useEffect(() => {
        if (open) setDialogOpen(open);
        if (announcementTitle) setTitle(announcementTitle);
        if (announcementDescription) setDescription(announcementDescription);
        if (announcementType) setType(announcementType);
        if (announcementResolved) setResolved(announcementResolved);
    }, [announcementDescription, announcementResolved, announcementTitle, announcementType, open]);

    return (
        <Dialog open={dialogOpen} onClose={onCancel} maxWidth="xs" fullWidth>
            <DialogTitleWithCloseIcon
                title={id ? 'Edit announcement' : 'Create new announcement'}
                handleClose={onCancel}
            />
            <DialogContent>
                {loading ? (
                    <CircularProgress color="inherit" />
                ) : (
                    <Stack direction="column" spacing={4}>
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <TextField
                                error={titleError}
                                onBlur={() => {
                                    if (!title) setTitleError(true);
                                    else setTitleError(false);
                                }}
                                id="standard-basic"
                                label="Announcement Title"
                                placeholder="New Announcement Title"
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                    if (e.target.value) setTitleError(false);
                                }}
                                value={title}
                                helperText={titleError ? 'Cannot be empty' : undefined}
                                sx={{ flex: 1 }}
                            />
                            <FormControlLabel
                                label="Mark as resolved"
                                disabled={!isNumber(id)}
                                control={
                                    <Checkbox checked={resolved} onChange={() => setResolved((state) => !state)} />
                                }
                            />
                        </Stack>
                        <FormControl fullWidth>
                            <InputLabel id="announcement-type-label">Announcement Type</InputLabel>
                            <AnnouncementTypeSelect
                                labelId="announcement-type-label"
                                id="announcement-type-select"
                                value={type}
                                label="Announcement Type"
                                onChange={(e) => {
                                    setType(e.target.value as AnnouncementTypeEnum);
                                }}
                            >
                                <AnnouncementTypeItem value={'Information'}>
                                    <Info fontSize="small" color="info" />
                                    Information
                                </AnnouncementTypeItem>
                                <AnnouncementTypeItem value={'Maintenance'}>
                                    <BuildCircle fontSize="small" color="info" />
                                    Maintenance
                                </AnnouncementTypeItem>
                                <AnnouncementTypeItem value={'MinorIncident'}>
                                    <ErrorIcon fontSize="small" color="warning" />
                                    Minor Incident
                                </AnnouncementTypeItem>
                                <AnnouncementTypeItem value={'MajorIncident'}>
                                    <NewReleases fontSize="small" color="error" />
                                    Major Incident
                                </AnnouncementTypeItem>
                            </AnnouncementTypeSelect>
                        </FormControl>
                        <TextField
                            id="outlined-multiline-flexible"
                            label="Description"
                            multiline
                            maxRows={10}
                            onChange={(e) => {
                                setDescription(e.target.value);
                                if (e.target.value) setDescriptionError(false);
                            }}
                            helperText={descriptionError ? 'Cannot be empty' : undefined}
                            value={description}
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="text" onClick={onCancel}>
                    Cancel
                </Button>
                <Button disabled={!canSave()} color="info" onClick={handleSaveAnnouncement}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const AnnouncementTypeSelect = styled(Select)(({ theme }) => ({
    '& div': {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
}));
const AnnouncementTypeItem = styled(MenuItem)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));
